import type {
  QuotationInvoiceDocumentItemDTO,
  QuotationInvoiceDocumentListItem,
  QuotationInvoiceDocumentPaymentDTO,
  QuotationInvoicePaymentAttachmentDTO,
} from "@/modules/quotation-invoice/domain/types";

export type ReceiptPaymentRecord = {
  amount: number;
  paidAt: string | null;
  method: string | null;
  notes: string | null;
  previouslyPaid: number;
  invoiceTotal: number;
  invoiceSubtotal: number;
  invoiceDiscountAmount: number;
  invoiceTaxAmount: number;
  remaining: number;
  attachments: QuotationInvoicePaymentAttachmentDTO[];
};

export function assertItemsReadyForPdfPrint(
  items: QuotationInvoiceDocumentItemDTO[],
) {
  for (const item of items) {
    if (!item.description.trim()) {
      throw new Error("PDF line missing description");
    }
    if (!(item.quantity > 0)) {
      throw new Error("PDF line missing quantity");
    }
    if (!Number.isFinite(item.unitPrice) || !Number.isFinite(item.lineTotal)) {
      throw new Error("PDF line missing numeric totals");
    }
  }
}

export function buildReceiptPaymentRecord(
  doc: QuotationInvoiceDocumentListItem,
  payments: QuotationInvoiceDocumentPaymentDTO[],
): ReceiptPaymentRecord | null {
  if (doc.documentType !== "receipt" || !doc.paymentId) return null;
  const payment = payments.find((p) => p.id === doc.paymentId);
  if (!payment) return null;

  function round2(n: number) {
    return Math.round(n * 100) / 100;
  }

  let previouslyPaid = 0;
  for (const p of payments) {
    if (p.id === payment.id) break;
    if (p.kind === "refund") previouslyPaid -= p.amount;
    else previouslyPaid += p.amount;
  }
  previouslyPaid = round2(Math.max(0, previouslyPaid));

  const invoiceTotal = doc.invoiceTotal ?? Number(doc.total);
  const invoiceSubtotal = doc.invoiceSubtotal ?? Number(doc.subtotal);
  const invoiceDiscountAmount =
    doc.invoiceDiscountAmount ?? Number(doc.discountAmount);
  const invoiceTaxAmount = doc.invoiceTaxAmount ?? Number(doc.taxAmount);
  const remaining = round2(
    invoiceTotal - (previouslyPaid + Number(payment.amount)),
  );

  return {
    amount: Number(payment.amount),
    paidAt: payment.paidAt,
    method: payment.method,
    notes: payment.notes,
    previouslyPaid,
    invoiceTotal,
    invoiceSubtotal,
    invoiceDiscountAmount,
    invoiceTaxAmount,
    remaining,
    attachments: payment.attachments,
  };
}
